"use client";

import { AlertCircle, CalendarCheck, ChefHat, Link, Sparkles } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { Dialog } from "@/components/shared/Dialog";
import { UpgradeButton } from "@/components/shared/UpgradeButton";
import { trackSubscription } from "@/lib/analytics";
import { getDisplayPriceInterval, getYearlySavingsPercent } from "@/lib/pricing";

export type LimitType = "ai" | null;

interface PremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType?: LimitType;
}

export function PremiumModal({ isOpen, onClose, limitType = null }: PremiumModalProps) {
  const t = useTranslations("premium");
  const locale = useLocale();
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">("monthly");
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Track when modal opens
  useEffect(() => {
    if (isOpen) {
      trackSubscription("modal_opened", { limitType });
    }
  }, [isOpen, limitType]);

  const getLimitMessage = () => {
    if (!limitType) return null;

    if (limitType === "ai") {
      return {
        title: t("aiLimitTitle"),
        message: t("aiLimitMessage"),
      };
    }

    return null;
  };

  const limitMessage = getLimitMessage();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      disableOutsideClick={isUpgrading}
      title={limitMessage ? t("upgradeTitle") : t("upgradeToPremium")}
    >
      {/* Billing Interval Toggle */}
      <div className="flex items-center justify-center gap-2 p-1 bg-muted rounded-lg">
        <button
          onClick={() => setBillingInterval("monthly")}
          disabled={isUpgrading}
          className={`flex-1 cursor-pointer px-4 py-2.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            billingInterval === "monthly"
              ? "bg-background shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          aria-label={t("selectMonthlyBilling")}
          aria-pressed={billingInterval === "monthly"}
        >
          <div className="flex flex-col items-center gap-0.5">
            <span>{t("monthly")}</span>
            <span className="text-xs font-semibold">
              {getDisplayPriceInterval("monthly", t, locale)}
            </span>
          </div>
        </button>
        <button
          onClick={() => setBillingInterval("yearly")}
          disabled={isUpgrading}
          className={`flex-1 cursor-pointer px-4 py-2.5 rounded-md text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            billingInterval === "yearly"
              ? "bg-background shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          aria-label={t("selectYearlyBilling")}
          aria-pressed={billingInterval === "yearly"}
        >
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1">
              <span>{t("yearly")}</span>
              <span className="text-xs text-primary font-semibold">
                {t("savePercent", {
                  percent: getYearlySavingsPercent(),
                })}
              </span>
            </div>
            <span className="text-xs font-semibold">
              {getDisplayPriceInterval("yearly", t, locale)}
            </span>
          </div>
        </button>
      </div>

      {/* Limit Message (if provided) */}
      {limitMessage && (
        <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-orange-900 dark:text-orange-100">
              {limitMessage.title}
            </h3>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              {limitMessage.message}
            </p>
          </div>
        </div>
      )}

      {/* Features List */}
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium">{t("moreAiCredits")}</h3>
            <p className="text-sm text-muted-foreground">{t("moreAiCreditsDesc")}</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <ChefHat className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium">{t("aiRecipeEditing")}</h3>
            <p className="text-sm text-muted-foreground">{t("aiRecipeEditingDesc")}</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Link className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium">{t("aiUrlImport")}</h3>
            <p className="text-sm text-muted-foreground">{t("aiUrlImportDesc")}</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <CalendarCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium">{t("prepAhead")}</h3>
            <p className="text-sm text-muted-foreground">{t("prepAheadDesc")}</p>
          </div>
        </div>
      </div>

      {/* CTA Button */}
      <UpgradeButton
        billingInterval={billingInterval}
        limitType={limitType}
        className="w-full h-11 cursor-pointer gap-2"
        onLoadingChange={setIsUpgrading}
      />
    </Dialog>
  );
}
